import React, { useEffect, useState,useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Table from '../../../../components/Table';

import Pagination from '../../../../components/Pagination';
import ReadOnlyInput from '../../../../components/ReadOnlyInput';

import PatientSignatureModal from '../../../global/view/patientSignature';
import DoctorePrescription from '../../../global/view/doctorePrescription';
import ClaimStatus from '../../../global/view/ClaimStatus';
import ClaimApprovedTable from '../../../global/view/ClaimApprovedTable';
import PharmacistSignatureModal from '../../../global/view/PharmacistSignatureModal';

import ActionTile from '../components/ActionTile';
import FeeSummaryCard from '../components/FeeSummaryCard';
import DeliveryOptions from '../components/DeliveryOptions';
import styles from '../css/ChronicScriptView.module.css';
import { saveRepeatDrugs, signRepeat } from '../../../../api/dispensary/script/processScriptApi';

import { useAuth } from '../../../../context/AuthContext';
import { useCompanyAndDispensingFee } from '../utils/useCompanyAndDispensingFee';
import { useScriptPatientProfile } from '../utils/useScriptPatientProfile';
import { useScriptTableData } from '../utils/useScriptTableData';
import { buildPrescription, calculatePricing } from '../utils/pricingUtils';

const ChronicScriptView = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { user } = useAuth();

    const { scriptData, continueAfterMedicalAid } = location.state || {};
    const baseState = location.state || {};

    const [showPatientSig, setShowPatientSig] = useState(false);
    const [pharmacistSig, setPharmacistSig] = useState(null);
    const [patientSig, setPatientSig] = useState(null);
    const [showDoctorModal, setShowDoctorModal] = useState(false);
    const [showPharmacistSig, setShowPharmacistSig] = useState(false);
    const [showClaimStatus, setShowClaimStatus] = useState(false);
    const [showClaimApproved, setShowClaimApproved] = useState(false);
    const [progressPercent, setProgressPercent] = useState(0);
    const [mode, setMode] = useState('Delivery');
    const [receiptNumber, setReceiptNumber] = useState(null);
    const rowsPerPage = 4;

    const todayIsoDate = useMemo(() => {
        try {
            return new Date().toISOString().split('T')[0];
        } catch {
            return undefined;
        }
    }, []);

    const { companyName, pharmacyRegNumber, dispensingFee } = useCompanyAndDispensingFee(user?.branchcode);
    const patientProfile = useScriptPatientProfile(scriptData);
    const {
        tableData,
        currentPage,
        setCurrentPage,
        pagedData,
        totalPages,
    } = useScriptTableData(scriptData, rowsPerPage);

    const isMedicalAid = (scriptData?.paymentType || '').toLowerCase() === 'medical aid';

    // On landing, if Medical Aid, immediately show ClaimStatus (medical-aid1.png flow)
    useEffect(() => {
        if (isMedicalAid && !continueAfterMedicalAid) {
            setProgressPercent(0);
            setShowClaimStatus(true);
        }
    }, [isMedicalAid, continueAfterMedicalAid]);

    // If coming back from ClaimApprovedTable, set pharmacist signature and continue with patient signature flow
    useEffect(() => {
        if (continueAfterMedicalAid) {
            if (location.state?.pharmacistSignature) {
                setPharmacistSig(location.state.pharmacistSignature);
            }
            setShowClaimStatus(false);
            setShowPatientSig(true);
        }
    }, [continueAfterMedicalAid, location.state]);

    if (!scriptData) {
        return <div>Loading...</div>;
    }

    const rawScriptNumber = (() => {
        const navType = baseState.type || scriptData.type;

        // If processing a script, prefer the process script number explicitly
        if (navType === 'process') {
            return (
                baseState.processscriptnumber ??
                scriptData.processscriptnumber ??
                scriptData.processScriptNumber ??
                baseState.scriptNo ??
                scriptData.scriptNo ??
                scriptData.scriptNumber ??
                baseState.scriptNumber ??
                ''
            );
        }

        // If clinic flow, prefer clinic reference explicitly
        if (navType === 'clinic') {
            return (
                baseState.clinicreference ??
                scriptData.clinicreference ??
                scriptData.clinicReference ??
                ''
            );
        }

        // Fallback: previous heuristic
        return (
            baseState.scriptNumber ??
            baseState.processscriptnumber ??
            baseState.scriptNo ??
            scriptData.processscriptnumber ??
            scriptData.processScriptNumber ??
            scriptData.scriptNo ??
            scriptData.scriptNumber ??
            ''
        );
    })();

    const scriptNumberDisplay = rawScriptNumber != null ? String(rawScriptNumber) : '';

    const patientLabel = scriptData?.patient || '';
    const profileLabelFromData = scriptData?.profileNo || '';
    const profileFromLabelMatch = /\(([^()]+)\)\s*$/.exec(patientLabel || '');
    const profileLabel = profileLabelFromData || (profileFromLabelMatch ? profileFromLabelMatch[1] : '');

    const scriptAddressLines = scriptData?.addressLines || [];
    const profileAddressLines =
        patientProfile && Array.isArray(patientProfile.addressLines)
            ? patientProfile.addressLines
            : [];

    const scriptPhone = scriptData?.phone || '';
    const profilePhone = (patientProfile?.phone && String(patientProfile.phone).trim()) || '';

    const patientInfo = {
        name: patientLabel || 'Patient',
        patient: patientLabel || '',
        profile: profileLabel || (patientProfile?.profileNo || ''),
        address:
            (Array.isArray(scriptAddressLines) && scriptAddressLines.length
                ? scriptAddressLines
                : profileAddressLines),
        phone: scriptPhone || profilePhone || '',
        medicalAid:
            (patientProfile?.medicalAid && String(patientProfile.medicalAid).trim())
                || scriptData?.medicalAidNumber
                || '',
    };

    const { dispensingFeeValue, grossWithDispensing, grossAmount } = calculatePricing(scriptData, dispensingFee);

    const prescription = buildPrescription(
        scriptData,
        companyName,
        pharmacyRegNumber,
        grossAmount,
    );

    // Debug logging to inspect what the preview modal receives
    console.log('[ChronicScriptView] preview data', {
        scriptData,
        patientProfile,
        patientInfo,
        prescription,
    });

    const repeatId = baseState.repeatId || scriptData?.repeatId || null;

    // Debug: Log receipt number state
    console.log('[ChronicScriptView] Current receipt number state:', receiptNumber);
    console.log('[ChronicScriptView] Script data:', scriptData);
    console.log('[ChronicScriptView] Repeat ID:', repeatId);

    const handlePackageAndLabel = async () => {
        if (!repeatId) {
            return;
        }
        const items = (scriptData?.drugs || [])
            .map((d) => ({
                prescriptionDrugId: d.prescriptionDrugId,
                quantity: d.quantity ?? d.qty ?? null,
                status: 'Dispensed',
            }))
            .filter((it) => it.prescriptionDrugId);

        if (!items.length) {
            return;
        }

        try {
            await saveRepeatDrugs({ repeatid: repeatId, items });
        } catch (e) {
            console.error('Failed to save repeat drugs', e);
        }
    };

    const handleMedicationLabel = () => {
        console.log('[ChronicScriptView] ========== MEDICATION LABEL CLICKED ==========');
        if (!scriptData) {
            console.log('[ChronicScriptView] ERROR: No script data available');
            return;
        }

        console.log('[ChronicScriptView] handleMedicationLabel called');
        console.log('[ChronicScriptView] Current receipt number:', receiptNumber);
        console.log('[ChronicScriptView] Gross with dispensing:', grossWithDispensing);
        console.log('[ChronicScriptView] Gross amount:', grossAmount);
        console.log('[ChronicScriptView] Script data total:', scriptData?.total);

        // Extract ICD-10 codes from drugs
        const icd10Codes = (scriptData.drugs || []).map(drug => drug.icd10).filter(Boolean);
        console.log('[ChronicScriptView] Extracted ICD-10 codes:', icd10Codes);
        
        // Create label data structure
        const labelData = (scriptData.drugs || []).map((drug, index) => ({
            id: uuidv4(),
            data: {
                patientName: scriptData.patient || "",
                medication: drug.drugName || "",
                quantity: drug.qty || drug.quantity || "",
                prescriber: scriptData.pharmacist || "",
                rxNumber: scriptNumberDisplay || "",
                directions: drug.instructions || "",
                packSize: drug.packSize || "",
                icd10: icd10Codes[index] || (drug.icd10 || ""),
                practitioner: scriptData.practitioner || "",
                barcode: "", // Will be populated from inventory search
                date: new Date().toISOString().split('T')[0],
            },
            isDrug: true,
        }));

        console.log('[ChronicScriptView] Created label data:', labelData);

        // Create medications array for dropdown
        const medicationsArray = (scriptData.drugs || []).map((drug, index) => ({
            id: `drug-${index}`,
            name: drug.drugName || '',
            quantity: drug.qty || drug.quantity || '',
            packSize: drug.packSize || '',
            barcode: "", // Will be populated from inventory search
            icd10Codes: icd10Codes[index] ? [icd10Codes[index]] : (drug.icd10 ? [drug.icd10] : [])
        }));

        console.log('[ChronicScriptView] Created medications array:', medicationsArray);

        // Navigate to label configuration page with data
        const targetUrl = scriptNumberDisplay
            ? `/dispensary/label-configuration?prescriptionId=${scriptNumberDisplay}&icd10Codes=${icd10Codes.join(',')}`
            : `/dispensary/label-configuration?icd10Codes=${icd10Codes.join(',')}`;

        const navigationState = {
            labelData: labelData,
            medications: medicationsArray,
            scriptNumber: scriptNumberDisplay || "",
            pharmacyInfo: {
                name: "DURBAN NORTH PHY",
                address: "DOCTOR MEDICAL CENTRE35 UMHLANGA ROCKS DRIVE 0315637577",
                phone: "",
            },
            receiptNumber: receiptNumber,
            totals: {
                total: grossWithDispensing || grossAmount || scriptData?.total || 0
            }
        };

        console.log('[ChronicScriptView] ========== NAVIGATION DETAILS ==========');
        console.log('[ChronicScriptView] Target URL:', targetUrl);
        console.log('[ChronicScriptView] Navigation state being passed:', navigationState);
        console.log('[ChronicScriptView] Receipt number in navigation state:', navigationState.receiptNumber);
        console.log('[ChronicScriptView] Totals in navigation state:', navigationState.totals);

        try {
            navigate(targetUrl, {
                state: navigationState
            });
            console.log('[ChronicScriptView] Navigation executed successfully');
        } catch (error) {
            console.error('[ChronicScriptView] Navigation failed:', error);
        }
    };

    const handleViewScriptDocument = () => {
        const url =
            scriptData?.scriptDocUrl ||
            scriptData?.docurl ||
            '';

        if (url && typeof window !== 'undefined') {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            console.log('[ChronicScriptView] No script document URL available for this prescription');
        }
    };

    return (
        <div className={styles.page}>
            <Navbar backTo="/customer/dispensary/chronic-medication" />
            <main className={styles.main}>
                {showClaimApproved ? (
                    <ClaimApprovedTable
                        embed
                        scriptData={scriptData}
                        onInPlaceContinue={(sig) => {
                            setPharmacistSig(sig);
                            setShowClaimApproved(false);
                            setShowPatientSig(true);
                        }}
                    />
                ) : (
                    <>
                        {/* Top controls */}
                        <div className={styles.controlsRow}>
                            <ReadOnlyInput label="Script Number" value={scriptNumberDisplay} />

                            <ReadOnlyInput label="Total Items" value={scriptData.totalItems ?? tableData.length} />
                            <ReadOnlyInput label="Pharmacist" value={scriptData.pharmacist || 'N Khan'} />
                            <ReadOnlyInput label="Payment Type" value={scriptData.paymentType || 'Cash'} />
                            <ReadOnlyInput label="Practitioner" value={scriptData.practitioner || 'M Moodley - 689523'} />
                        </div>

                        {/* Table */}
                        <div className={styles.tableCard}>
                            <Table
                                columns={[
                                    { header: 'Drug Name', accessor: 'drugName' },
                                    { header: 'Pack Size', accessor: 'packSize' },
                                    { header: 'ICD-10', accessor: 'icd10' },
                                    { header: 'Strength', accessor: 'strength' },
                                    { header: 'Qty', accessor: 'qty' },
                                    { header: 'Nappi Code', accessor: 'nappiCode' },
                                    { header: 'Instructions', accessor: 'instructions' },
                                    { header: 'Cost Price', accessor: 'costPrice' },
                                ]}
                                data={pagedData}
                            />
                        </div>

                        <br></br>

                        {/* Actions left; delivery middle; fees right */}
                        <div className={styles.midRow}>
                            <div className={styles.tilesMiddle}>
                                <div className={styles.tilesCol}>
                                    <ActionTile
                                        title="Script"
                                        rightLinkLabel="View"
                                        onRightLinkClick={handleViewScriptDocument}
                                    />
                                    <ActionTile title="Sign Invoice" onClick={() => setShowPharmacistSig(true)} />
                                </div>
                            </div>
                            <div className={styles.optionsRight}>
                                <DeliveryOptions
                                    mode={mode}
                                    onModeChange={setMode}
                                    minDate={todayIsoDate}
                                />
                            </div>
                            <div className={styles.feesLeft}>
                                <FeeSummaryCard
                                    dispensingFee={
                                        Number.isFinite(dispensingFeeValue) && dispensingFeeValue > 0
                                            ? `R ${dispensingFeeValue.toFixed(2)}`
                                            : (scriptData.dispensingFee || 'R0.00')
                                    }
                                    gross={
                                        grossAmount
                                            ? `R ${grossAmount}`
                                            : (scriptData.gross || 'R219.50')
                                    }
                                />
                            </div>
                        </div>

                        {/* Bottom buttons */}
                        <div className={styles.bottomButtons}>
                            <button className={styles.btnPrimary} onClick={handleMedicationLabel}>
                                Medication Label
                            </button>
                            <button className={styles.btnPrimary}>
                                Delivery Label
                            </button>
                            <button className={styles.btnPrimary} onClick={() => navigate('/customer/dispensary/package-script')}>
                                Package and Label
                            </button>
                        </div>
                    </>
                )}
            </main>
            <Footer />

            <PatientSignatureModal
                isOpen={showPatientSig}
                onClose={() => setShowPatientSig(false)}
                onConfirm={async (dataUrl) => {
                    setPatientSig(dataUrl);

                    if (repeatId) {
                        const items = (scriptData?.drugs || [])
                            .map((d) => ({
                                prescriptionDrugId: d.prescriptionDrugId,
                                quantity: d.quantity ?? d.qty ?? null,
                                status: 'Dispensed',
                            }))
                            .filter((it) => it.prescriptionDrugId);

                        if (items.length) {
                            try {
                                await saveRepeatDrugs({ repeatid: repeatId, items });
                            } catch (e) {
                                console.error('Failed to save repeat drugs on patient sign', e);
                            }
                        }

                        try {
                            const signResponse = await signRepeat(repeatId, {
                                paymenttype: scriptData?.paymentType || null,
                                gross: grossWithDispensing || null,
                                dispensingfee: dispensingFeeValue || null,
                                deliveryoption: mode || null,
                            });
                            
                            // Capture the receipt number from the response
                            if (signResponse?.data?.receiptNumber) {
                                setReceiptNumber(signResponse.data.receiptNumber);
                                console.log('[ChronicScriptView] Captured receipt number:', signResponse.data.receiptNumber);
                            }
                        } catch (e) {
                            console.error('Failed to sign repeat on patient sign', e);
                        }
                    }

                    setShowPatientSig(false);
                    setShowDoctorModal(true);
                }}
                initialSignature={patientSig}
                totals={{
                    amount: scriptData?.amount ?? 'R129.18',
                    vat: scriptData?.vat ?? 'R19.38',
                    total: scriptData?.total
                        ? `R${scriptData.total.toFixed?.(2) ?? scriptData.total}`
                        : 'R148.56',
                }}
            />

            <PharmacistSignatureModal
                isOpen={showPharmacistSig}
                onClose={() => setShowPharmacistSig(false)}
                onNext={(sig) => {
                    setPharmacistSig(sig);

                    setShowPharmacistSig(false);
                    if (isMedicalAid) {
                        setProgressPercent(0);
                        setShowClaimStatus(true);
                    } else {
                        setShowPatientSig(true);
                    }
                }}
                previewProps={{ patientInfo, prescription }}
            />

            <DoctorePrescription
                isOpen={showDoctorModal}
                onClose={() => setShowDoctorModal(false)}
                patientInfo={patientInfo}
                prescription={prescription}
                pharmacistSignature={pharmacistSig}
                patientSignature={patientSig}
                receiptNumber={receiptNumber}

                totals={{
                    vat: scriptData?.vat ?? 'R19.38',
                    total: scriptData?.total
                        ? `R${scriptData.total.toFixed?.(2) ?? scriptData.total}`
                        : 'R148.56',
                }}
                useCompletedPopup={true}
            />

            {isMedicalAid && (
                <ClaimStatus
                    isOpen={showClaimStatus}
                    onClose={() => setShowClaimStatus(false)}
                    scriptData={scriptData}
                    claimDetails={{ progressPercent }}
                />
            )}
        </div>
    );
};

export default ChronicScriptView;
